/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useState, useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';
import PropTypes from 'prop-types';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import Switch from '@material-ui/core/Switch';
import { useRouter } from 'next/router';
import { setSelectedNav } from '@actions/educator';
import CustomMenuItem from '@comp/Dropdown/CustomMenuItem';
import Avatar from '@base/Avatar/Avatar';
import H7 from '@base/H7/H7';
import LinearProgressBar from '@base/LinearProgressBar/LinearProgressBar';
import Menu from '@base/Menu/Menu';
import Link from '@base/Link/Link';
import P1 from '@base/P1/P1';
import P2 from '@base/P2/P2';
import Grow from '@base/Grow/Grow';
import ArrowBack from '@icon/ArrowBack';
import parseNumber from '@utils/parseNumber';
import cookies from '@utils/cookies';
import textHelper from '@utils/textHelper';
import { setDarkMode, logout } from '@actions/auth';
import { dashboardTouchpointEvent } from '@events/sendAnalytics';
import analyticsAction, { segment } from '@actions/analytics';
import { REFERRAL_TOUCHPOINT_CLICKED } from '@constants/segment';
import Dynamic from '@base/Dynamic/Dynamic';
import { localStorageAvailable } from '@utils/localStorage';
import { Spacings, Fonts } from '@styles/index';
import useEducatorContext from '@hooks/useEducatorContext';
import { useFlagSelector } from '@hooks/useLayoutOptions';

const MeDashboard = Dynamic(() => import('@cont/MeDashboard/MeDashboard'));

const StyledDiv = styled.div`
  grid-column: 21/25;
  align-self: center;
  display: flex;
  justify-content: space-between;
  @media (max-width: 70.95em) {
    grid-column: 19/25;
  }
  @media (max-width: 47.95em) {
    justify-content: flex-end;
    display: none;
  }
`;

const LearnedMinutes = styled.div`
  margin: auto 0;
  cursor: pointer;
  visibility: ${({ isEducatorUrl }) => isEducatorUrl && 'hidden'};
  @media (max-width: 47.95em) {
    display: none;
  }
`;

const Label = styled(P1)`
  font-size: 14px;
  line-height: 150%;
  color: ${({ selected, theme }) =>
    selected ? theme.colors.green : theme.colors.fontGrey};
  flex-grow: 1;
  display: block;
  font-weight: ${({ selected }) => (selected ? 'bold' : 'initial')};
`;

const TitledIcon = styled.div`
  transform: rotate(135deg);
`;

const ProfileTop = styled.div`
  padding: 16px 16px;
  display: flex;
`;

const ProfileTopData = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: ${Spacings.SPACING_3B};
`;

const Subtitle = styled(P2)`
  color: ${({ color }) => color};
  margin-top: ${Spacings.SPACING_1B};
  font-size: ${Fonts.EXTRA_SMALL};
`;

const StyledLinearProgress = styled(LinearProgressBar)`
  margin-top: 8px;
  color: ${({ theme }) => theme.colors.dropShadowV2};
`;

const UserAvatar = styled(Avatar)`
  @media only screen and (max-width: 540px) {
    pointer-events: none;
  }
`;

const HeaderUser = ({ toggleDarkMode, excludeOptions }) => {
  const theme = useContext(ThemeContext);
  const isEducatorSide = useEducatorContext();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [showDashboard, setShowDashboard] = useState(false);

  const handleAvatarOnClick = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const onCloseDashboard = () => {
    setShowDashboard(false);
  };

  const handleMenuClose = () => setAnchorEl(null);
  const authorisation = useSelector(({ auth }) => auth ?? {}, shallowEqual);
  const { me, accessControl } = authorisation;
  const dailyTask = useSelector(
    ({ auth }) => auth.dailyTask ?? {},
    shallowEqual
  );
  let isDarkModeOn = useSelector(({ auth }) => auth.isDarkModeOn);

  const handleSwitchChange = () => {
    const newFlag = !isDarkModeOn;
    isDarkModeOn = !isDarkModeOn;
    cookies.setCookie('isDarkModeOn', newFlag ? 'true' : '');
    dispatch(setDarkMode(newFlag));
    toggleDarkMode(newFlag);
  };
  const { name, username, isEducator, isModerator } = me;
  const {
    is_doubt_topology_present,
    is_reviewer,
    is_evaluator,
    is_external_content_uploader
  } = accessControl || {};
  let duration = parseNumber(dailyTask && dailyTask.duration / 60);
  if (duration < 0) duration = 0;

  const showMydashboard =
    !isEducator &&
    (is_doubt_topology_present ||
      is_reviewer ||
      is_evaluator ||
      is_external_content_uploader);

  const getRedirection = () => {
    if (is_doubt_topology_present)
      return { href: '/educator/doubts', as: '/educator/doubts' };
    if (is_reviewer)
      return { href: '/educator/test/review', as: '/educator/test/review' };
    if (is_evaluator)
      return { href: '/educator/test/evaluate', as: '/educator/test/evaluate' };
    if (is_external_content_uploader)
      return { href: '/educator/test/upload', as: '/educator/test/upload' };
  };

  const getPath = () => {
    if (isEducator || isModerator) return '/[authorId]';
    return '/learner/[learnerUID]';
  };

  let options = [
    {
      label: 'Profile',
      value: 'profile',
      url: { href: getPath(), as: me.relativeLink }
    },
    {
      label: 'Settings',
      value: 'settings',
      url: isEducator
        ? { href: '/educator/settings', as: '/educator/settings' }
        : { href: '/settings', as: '/settings' }
    }
  ];

  if (isModerator)
    options.push({
      label: 'Moderator dashboard',
      value: 'mod',
      url: {
        href: '/moderator/dashboard/[type]',
        as: '/moderator/dashboard/educators'
      },
      newTab: true
    });

  if (isEducator)
    options.push({
      label: 'Educator dashboard',
      value: 'dashboard',
      url: { href: '/educator', as: '/educator' },
      newTab: true
    });

  if (showMydashboard)
    options.push({
      label: 'My dashboard',
      value: 'mydashboard',
      url: getRedirection(),
      newTab: true
    });

  if (me.plusSubscriptionUids.length)
    options.push({
      label: 'Refer your friends',
      value: 'refer',
      url: {
        href: { pathname: '/settings', query: { refer: true } },
        as: '/settings?refer=true'
      }
    });

  if (!excludeOptions.includes('darkMode')) {
    options.push({
      label: 'Dark mode',
      value: 'darkMode',
      url: { href: null, as: null },
      shwBtn: true,
      btn: (
        <Switch
          checked={isDarkModeOn}
          onChange={handleSwitchChange}
          value="checkedA"
          color="primary"
        />
      )
    });
  }
  options.push({
    label: 'Sign out',
    value: 'signOut'
  });

  const noDarkMode = [
    '/quiz/[quizSlug]/[quizUID]',
    '/quiz/[quizSlug]/[quizUID]/solutions/[sessionUID]'
  ];

  const router = useRouter();

  if (noDarkMode.includes(router.pathname) || isEducatorSide) {
    options = options.filter((option) => option.value !== 'darkMode');
  }

  const signOutUser = () => {
    cookies.eraseCookie('accessToken');
    cookies.eraseCookie('refreshToken');
    cookies.eraseCookie('selectedGoalUid');
    cookies.eraseCookie('isDarkModeOn');
    cookies.eraseCookie('referral-player');
    cookies.eraseCookie('referral-banners');
    cookies.eraseCookie('jwtToken');
    if (localStorageAvailable()) localStorage?.clear();
    // Hard Refresh to clear redux store
    window.location.replace('/');
  };

  const handleOptionClick = (optionIndex) => {
    handleMenuClose();
    const selectedOption = options[optionIndex];
    if (selectedOption.value === 'signOut') {
      window.analytics?.reset();
      window.Intercom?.('shutdown');
      window.webengage?.user?.logout();
      signOutUser();
      dispatch(logout());
    } else if (selectedOption.value === 'darkMode') {
      const newFlag = !isDarkModeOn;
      cookies.setCookie('isDarkModeOn', newFlag ? 'true' : '');
      dispatch(setDarkMode(newFlag));
      toggleDarkMode(newFlag);
    } else if (selectedOption.value === 'mod')
      dashboardTouchpointEvent(selectedOption.label, me?.uid, me?.username);
    else if (selectedOption.value === 'dashboard') {
      dashboardTouchpointEvent(selectedOption.label, me?.uid, me?.username);
      dispatch(setSelectedNav('dashboard'));
    } else if (selectedOption.value === 'help-center') {
      dispatch(
        analyticsAction('help-center-clicked', '', null, {
          location: 'header user drop-down',
          path: router.asPath
        })
      );
    } else if (selectedOption.value === 'refer') {
      segment.track(REFERRAL_TOUCHPOINT_CLICKED, {
        last_primary_source: 'Profile Dropdown'
      });
    }
  };

  const anchorStyle =
    'display: flex; justify-content: space-between; width: 100%';

  const hideLearnerMinutes = useFlagSelector('hideLearnerMinutes');

  return (
    <StyledDiv>
      {!hideLearnerMinutes ? (
        <LearnedMinutes
          onClick={() => setShowDashboard(true)}
          isEducatorUrl={isEducatorSide}
        >
          <H7 color={theme.colors.fontGrey}>
            {`${textHelper.pluralize(duration, 'minute')} today`}
          </H7>
          <StyledLinearProgress value={dailyTask?.percentage_completed} />
        </LearnedMinutes>
      ) : (
        <LearnedMinutes />
      )}
      <UserAvatar
        size="40px"
        src={me.thumbnail}
        onClick={handleAvatarOnClick}
      />

      <Menu
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        position="right"
        height="auto"
      >
        <ProfileTop>
          <Avatar size="40px" src={me.thumbnail} />
          <ProfileTopData>
            <H7 color={theme.colors.fontGrey}>{name}</H7>
            <Subtitle color={theme.colors.fontLightGrey}>
              {`@${username}`}
            </Subtitle>
          </ProfileTopData>
        </ProfileTop>
        {options.map(
          (
            { url = {}, showArrow, label, value, btn, shwBtn, newTab },
            index
          ) => {
            return (
              <CustomMenuItem key={value}>
                {shwBtn && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      position: 'relative',
                      width: '100%'
                    }}
                  >
                    <div onClick={handleSwitchChange}>
                      <Label>{label}</Label>
                    </div>
                    <Grow />
                    <div style={{ position: 'absolute', right: '-12px' }}>
                      {btn}
                    </div>
                  </div>
                )}
                {!shwBtn && (
                  <Link
                    anchorStyle={anchorStyle}
                    disabled={!url.href}
                    href={url.href}
                    as={url.as}
                    onClick={() => handleOptionClick(index)}
                    newTab={newTab}
                  >
                    {!shwBtn && <Label>{label}</Label>}

                    {showArrow && (
                      <TitledIcon>
                        <ArrowBack size="18px" />
                      </TitledIcon>
                    )}
                  </Link>
                )}
              </CustomMenuItem>
            );
          }
        )}
      </Menu>
      {showDashboard && (
        <MeDashboard showDashboard={showDashboard} onClose={onCloseDashboard} />
      )}
    </StyledDiv>
  );
};

export default HeaderUser;

HeaderUser.propTypes = {
  toggleDarkMode: PropTypes.func.isRequired,
  excludeOptions: PropTypes.arrayOf(PropTypes.string)
};

HeaderUser.defaultProps = {
  excludeOptions: []
};
